import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ApprovalQueue } from '@/components/approvals/ApprovalQueue';
import { CheckCircle2, Clock, History, AlertCircle } from 'lucide-react';

/**
 * ApprovalDashboardPage
 * 
 * Main approval interface for hotel managers/owners to review and
 * approve/reject agent-requested actions.
 */
const ApprovalDashboardPage = () => {
  const { hotelId } = useParams();
  
  // TODO: Replace with actual auth user data
  const currentUser = {
    id: 'manager_john',
    role: 'manager',
    email: 'john@hotel.com'
  };

  return (
    <div className="min-h-screen bg-background admin-theme">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-[hsl(var(--web3-primary))]" />
              <div>
                <h1 className="text-xl font-bold heading-font">Approval Dashboard</h1>
                <p className="text-xs text-muted-foreground">Review & approve agent actions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to={`/admin/blockchain/approvals/${hotelId}/history`}>
                  <History className="h-4 w-4 mr-2" />
                  View History
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to="/admin/blockchain">
                  Back to Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="blockchain-header-gradient rounded-xl p-8 mb-8 border">
          <h2 className="text-3xl md:text-4xl font-bold heading-font mb-2">
            Pending Approvals
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            Review and approve agent-requested actions that require {currentUser.role} approval.
            Actions are executed immediately upon approval.
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Your Role</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-base">
                  {currentUser.role}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {currentUser.email}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Hotel ID</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-mono text-sm">{hotelId}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Only approvals for this hotel
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Auto-Expiration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-warning" />
                <span className="text-sm font-medium">8 hours</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Pending requests expire after 8 hours
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Approval Queue */}
        <Card>
          <CardHeader>
            <CardTitle className="heading-font">Approval Queue</CardTitle>
            <CardDescription>
              Review agent requests and approve or reject them. Approved actions are executed immediately.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="all">All Pending</TabsTrigger>
                <TabsTrigger value="manager">Manager Level</TabsTrigger>
                <TabsTrigger value="owner">Owner Level</TabsTrigger>
              </TabsList>
              
              <TabsContent value="all" className="mt-0">
                <ApprovalQueue 
                  hotelId={hotelId}
                  userRole={currentUser.role}
                  userId={currentUser.id}
                  userEmail={currentUser.email}
                />
              </TabsContent>
              
              <TabsContent value="manager" className="mt-0">
                <ApprovalQueue 
                  hotelId={hotelId}
                  userRole="manager"
                  userId={currentUser.id}
                  userEmail={currentUser.email}
                />
              </TabsContent>
              
              <TabsContent value="owner" className="mt-0">
                <ApprovalQueue 
                  hotelId={hotelId}
                  userRole="owner"
                  userId={currentUser.id}
                  userEmail={currentUser.email}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card className="mt-8 border-[hsl(var(--web3-primary))]/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-[hsl(var(--web3-primary))]" />
              How Approvals Work
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              <strong>When an agent (Bitsy, Marina, or Sage) attempts a high-risk operation:</strong>
            </p>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>The agent creates an approval request instead of executing immediately</li>
              <li>The request appears in this dashboard with full context</li>
              <li>A manager or owner reviews the request and approves/rejects</li>
              <li>If approved, the original tool is executed automatically</li>
              <li>If rejected, the action is blocked and logged for compliance</li>
              <li>Requests expire after 8 hours if not reviewed</li>
            </ol>
            <p className="pt-2">
              <strong>Common approval scenarios:</strong> Booking modifications, cancellations, refunds, room changes
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ApprovalDashboardPage;
